import React, { useState, useEffect } from 'react'
import { gql, useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  Container, Paper, Typography, Box, Button, Alert, CircularProgress,
  TextField, MenuItem, FormControlLabel, Switch, Chip, Divider, Grid, IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import StorefrontIcon from '@mui/icons-material/Storefront'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { restaurants } from '../apollo'
import { getQorMerchantKyc } from '../apollo/queries'
import { saveRestaurantKyc, boardRestaurantQor, refreshQorBoardingStatus } from '../apollo/mutations'

const RESTAURANTS = gql`${restaurants}`
const GET_KYC = gql`${getQorMerchantKyc}`
const SAVE_KYC = gql`${saveRestaurantKyc}`
const BOARD = gql`${boardRestaurantQor}`
const REFRESH = gql`${refreshQorBoardingStatus}`

const ENTITY_TYPES = [
  { v: 0, label: 'Sole Proprietor' },
  { v: 1, label: 'Corporation' },
  { v: 2, label: 'LLC' },
  { v: 3, label: 'Partnership' },
  { v: 5, label: 'Non-Profit' },
  { v: 6, label: 'Government' },
]
const STATUS_COLOR = { 1: 'success', 2: 'warning', 3: 'error', 4: 'error', 5: 'warning', 6: 'error' }

const emptyOwner = () => ({
  title: '', first: '', last: '', dob: '', email: '', phone: '', ownership: '',
  ssn: '', dl_num: '', dl_state: '', address1: '', city: '', state: '', zip: '',
})

export default function MerchantBoarding() {
  const initialId = new URLSearchParams(window.location.search).get('restaurantId') || ''
  const [restaurantId, setRestaurantId] = useState(initialId)
  const [form, setForm] = useState({})
  const [owners, setOwners] = useState([emptyOwner()])
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [boardInfo, setBoardInfo] = useState(null)
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [disputeCase, setDisputeCase] = useState('')

  const { data: restData, loading: restLoading } = useQuery(RESTAURANTS)
  const restList = restData?.restaurants ?? []

  const [loadKyc, { data: kycData, loading: kycLoading }] = useLazyQuery(GET_KYC, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (restaurantId) loadKyc({ variables: { restaurantId } })
  }, [restaurantId, loadKyc])

  // Hydrate the form when KYC loads.
  useEffect(() => {
    const k = kycData?.qorMerchantKyc
    if (!k) return
    setForm({
      qorEntityType: k.qorEntityType != null ? k.qorEntityType : 2,
      qorLegalName: k.qorLegalName || k.name || '',
      qorDescriptor: k.qorDescriptor || '',
      qorMcc: k.qorMcc || '5812',
      qorBusinessStartDate: k.qorBusinessStartDate || '',
      qorBusinessPhone: k.qorBusinessPhone || '',
      qorBusinessEmail: k.qorBusinessEmail || '',
      qorBusinessAddress1: k.qorBusinessAddress1 || '',
      qorBusinessAddress2: k.qorBusinessAddress2 || '',
      qorBusinessCity: k.qorBusinessCity || '',
      qorBusinessState: k.qorBusinessState || '',
      qorBusinessZip: k.qorBusinessZip || '',
      qorBusinessCountry: k.qorBusinessCountry || 'US',
      qorTimezone: k.qorTimezone || 'est',
      qorWebsite: k.qorWebsite || '',
      qorAnnualCcSales: k.qorAnnualCcSales || '',
      qorAvgTicket: k.qorAvgTicket || '',
      qorHighTicket: k.qorHighTicket || '',
      qorBankRoutingNumber: k.qorBankRoutingNumber || '',
      qorBankAccountMethod: k.qorBankAccountMethod || 10,
      qorEin: '', // never pre-filled; show "set" indicator instead
      qorBankAccountNumber: '',
    })
    try {
      const parsed = k.qorOwners ? JSON.parse(k.qorOwners) : []
      setOwners(parsed.length ? parsed.map((o) => ({ ...emptyOwner(), ...o, ssn: '' })) : [emptyOwner()])
    } catch (_) { setOwners([emptyOwner()]) }
    setAcceptTerms(!!k.qorTcAccepted)
    setBoardInfo({
      qorKycStatus: k.qorKycStatus, qorMerchantId: k.qorMerchantId, qorMid: k.qorMid,
      qorBoardingStatus: k.qorBoardingStatus, qorBoardingStatusLabel: k.qorBoardingStatusLabel,
      einSet: k.qorEinSet, bankSet: k.qorBankSet, bankLast4: k.qorBankAccountLast4,
    })
  }, [kycData])

  const [saveMutation, { loading: saving }] = useMutation(SAVE_KYC, {
    onCompleted: (d) => { setNotice(d.saveRestaurantKyc?.message || 'Saved.'); setError(''); refetchAfter(); },
    onError: (e) => setError(e.message),
  })
  const [boardMutation, { loading: boarding }] = useMutation(BOARD, {
    onCompleted: (d) => { setBoardInfo((b) => ({ ...b, ...d.boardRestaurantQor })); setNotice(d.boardRestaurantQor?.message || 'Boarding submitted.'); setError(''); refetchAfter(); },
    onError: (e) => setError(e.message),
  })
  const [refreshMutation, { loading: refreshing }] = useMutation(REFRESH, {
    onCompleted: (d) => { setBoardInfo((b) => ({ ...b, ...d.refreshQorBoardingStatus })); setNotice('Status refreshed.'); setError(''); },
    onError: (e) => setError(e.message),
  })

  function refetchAfter() {
    if (restaurantId) loadKyc({ variables: { restaurantId } })
    setTimeout(() => setNotice(null), 3000)
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const setOwner = (i, k) => (e) =>
    setOwners((arr) => arr.map((o, idx) => (idx === i ? { ...o, [k]: e.target.value } : o)))

  function onSave() {
    if (!restaurantId) return
    const kyc = { ...form, acceptTerms }
    // Only send EIN/bank account if the operator actually typed new values.
    if (!form.qorEin) delete kyc.qorEin
    if (!form.qorBankAccountNumber) delete kyc.qorBankAccountNumber
    // Owners → JSON string. Drop empty SSN so we don't overwrite a stored one.
    kyc.qorOwners = JSON.stringify(
      owners.map((o) => { const c = { ...o }; if (!c.ssn) delete c.ssn; return c })
    )
    saveMutation({ variables: { restaurantId, kyc } })
  }

  function authToken() {
    try {
      const vendor = localStorage.getItem('user-enatega')
      if (vendor) return JSON.parse(vendor).token
      const staff = localStorage.getItem('staff-session')
      if (staff) return JSON.parse(staff).token
    } catch (_) { /* ignore */ }
    return null
  }

  // Upload a KYB / dispute document straight to the backend, which forwards it
  // to QorCommerce's gwapi documents endpoint. `kind` is 'underwriting' | 'dispute'.
  async function uploadDoc(kind, file) {
    if (!file) return
    setError(''); setNotice(null); setUploading(true)
    try {
      const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8001'
      const path = kind === 'dispute' ? '/qor/documents/dispute' : '/qor/documents/underwriting'
      const fd = new FormData()
      fd.append('document', file)
      if (kind === 'dispute') fd.append('caseNumber', disputeCase)
      const token = authToken()
      const res = await fetch(`${SERVER_URL}${path}`, {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : {},
        body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || data.message || 'Upload failed.')
      }
      setNotice(data.message || 'Document uploaded.')
      setTimeout(() => setNotice(null), 3000)
    } catch (e) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  const status = boardInfo?.qorBoardingStatus
  const fieldSx = { mb: 0 }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <StorefrontIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Merchant Boarding</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Onboard a restaurant as a QorCommerce marketplace sub-merchant. Collect the
          business, ownership and payout-bank details, then submit for boarding. Once
          active, card payments split automatically to the restaurant.
        </Typography>

        <Box sx={{ mb: 3, maxWidth: 420 }}>
          <TextField
            select fullWidth size="small" label="Restaurant"
            value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}
            disabled={restLoading}
          >
            <MenuItem value=""><em>Pick a restaurant…</em></MenuItem>
            {restList.map((r) => (
              <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
            ))}
          </TextField>
        </Box>

        {kycLoading && <CircularProgress size={22} />}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {restaurantId && boardInfo && (
          <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip size="small" label={`KYC: ${boardInfo.qorKycStatus || 'NONE'}`} />
            {boardInfo.qorBoardingStatusLabel && (
              <Chip size="small" color={STATUS_COLOR[status] || 'default'} label={boardInfo.qorBoardingStatusLabel} />
            )}
            {boardInfo.qorMerchantId && <Typography variant="caption">Merchant: {boardInfo.qorMerchantId}</Typography>}
            {boardInfo.qorMid && <Typography variant="caption">MID: {boardInfo.qorMid}</Typography>}
          </Box>
        )}

        {restaurantId && (
          <>
            {/* Business */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Business</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Entity type" value={form.qorEntityType ?? 2} onChange={set('qorEntityType')} sx={fieldSx}>
                  {ENTITY_TYPES.map((t) => <MenuItem key={t.v} value={t.v}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Legal name" value={form.qorLegalName || ''} onChange={set('qorLegalName')} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Statement descriptor (≤20)" inputProps={{ maxLength: 20 }} value={form.qorDescriptor || ''} onChange={set('qorDescriptor')} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label={boardInfo?.einSet ? 'EIN / SSN (set — leave blank to keep)' : 'EIN / SSN (no dashes)'} value={form.qorEin || ''} onChange={set('qorEin')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="MCC" value={form.qorMcc || ''} onChange={set('qorMcc')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Start date (YYYY-MM-DD)" value={form.qorBusinessStartDate || ''} onChange={set('qorBusinessStartDate')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Phone" value={form.qorBusinessPhone || ''} onChange={set('qorBusinessPhone')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Email" value={form.qorBusinessEmail || ''} onChange={set('qorBusinessEmail')} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Address 1" value={form.qorBusinessAddress1 || ''} onChange={set('qorBusinessAddress1')} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Address 2" value={form.qorBusinessAddress2 || ''} onChange={set('qorBusinessAddress2')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="City" value={form.qorBusinessCity || ''} onChange={set('qorBusinessCity')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="State (2)" inputProps={{ maxLength: 2 }} value={form.qorBusinessState || ''} onChange={set('qorBusinessState')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="ZIP" value={form.qorBusinessZip || ''} onChange={set('qorBusinessZip')} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Website" value={form.qorWebsite || ''} onChange={set('qorWebsite')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" label="Annual CC sales (cents)" value={form.qorAnnualCcSales || ''} onChange={set('qorAnnualCcSales')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" label="Avg ticket (cents)" value={form.qorAvgTicket || ''} onChange={set('qorAvgTicket')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" label="High ticket (cents)" value={form.qorHighTicket || ''} onChange={set('qorHighTicket')} /></Grid>
            </Grid>

            {/* Owners */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Owners</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setOwners((a) => [...a, emptyOwner()])}>Add owner</Button>
            </Box>
            {owners.map((o, i) => (
              <Box key={i} sx={{ p: 2, mb: 2, border: '1px solid #eee', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Owner {i + 1}</Typography>
                  {owners.length > 1 && <IconButton size="small" onClick={() => setOwners((a) => a.filter((_, idx) => idx !== i))}><DeleteIcon fontSize="small" /></IconButton>}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Title" value={o.title} onChange={setOwner(i, 'title')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="First" value={o.first} onChange={setOwner(i, 'first')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Last" value={o.last} onChange={setOwner(i, 'last')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="DOB (mm/dd/yyyy)" value={o.dob} onChange={setOwner(i, 'dob')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Email" value={o.email} onChange={setOwner(i, 'email')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Phone (digits)" value={o.phone || ''} onChange={setOwner(i, 'phone')} helperText="Blank → business phone" /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Ownership %" value={o.ownership} onChange={setOwner(i, 'ownership')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="SSN (no dashes)" value={o.ssn} onChange={setOwner(i, 'ssn')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="DL number" value={o.dl_num} onChange={setOwner(i, 'dl_num')} /></Grid>
                  <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="DL state" inputProps={{ maxLength: 2 }} value={o.dl_state} onChange={setOwner(i, 'dl_state')} /></Grid>
                  <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="Address 1" value={o.address1} onChange={setOwner(i, 'address1')} /></Grid>
                  <Grid item xs={4} sm={3}><TextField fullWidth size="small" label="City" value={o.city} onChange={setOwner(i, 'city')} /></Grid>
                  <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="State" inputProps={{ maxLength: 2 }} value={o.state} onChange={setOwner(i, 'state')} /></Grid>
                  <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="ZIP" value={o.zip} onChange={setOwner(i, 'zip')} /></Grid>
                </Grid>
              </Box>
            ))}

            {/* Bank */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, mt: 1 }}>Payout bank account</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={5}><TextField fullWidth size="small" label={boardInfo?.bankSet ? `Account number (set ••••${boardInfo.bankLast4 || ''} — leave blank to keep)` : 'Account number'} value={form.qorBankAccountNumber || ''} onChange={set('qorBankAccountNumber')} /></Grid>
              <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Routing (9 digits)" inputProps={{ maxLength: 9 }} value={form.qorBankRoutingNumber || ''} onChange={set('qorBankRoutingNumber')} /></Grid>
              <Grid item xs={6} sm={3}>
                <TextField select fullWidth size="small" label="Account method" value={form.qorBankAccountMethod ?? 10} onChange={set('qorBankAccountMethod')}>
                  <MenuItem value={10}>Business checking</MenuItem>
                  <MenuItem value={8}>Checking</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* Documents (KYB underwriting + dispute evidence) */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, mt: 1 }}>Documents</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              PDF, JPEG or PNG · max 5 MB. Sent directly to QorCommerce (not stored on our servers).
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined" component="label" fullWidth
                  disabled={uploading} startIcon={<UploadFileIcon />}
                >
                  Upload underwriting (KYB) document
                  <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => { uploadDoc('underwriting', e.target.files[0]); e.target.value = '' }} />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  e.g. voided check, EIN letter, owner ID.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small" label="Dispute case #" value={disputeCase}
                    onChange={(e) => setDisputeCase(e.target.value)} sx={{ width: 160 }}
                  />
                  <Button
                    variant="outlined" component="label"
                    disabled={uploading || !disputeCase} startIcon={<UploadFileIcon />}
                  >
                    Upload evidence
                    <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => { uploadDoc('dispute', e.target.files[0]); e.target.value = '' }} />
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Chargeback evidence for a specific dispute case.
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={<Switch checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />}
              label="The restaurant has accepted the QorCommerce terms of service"
            />

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
              <Button variant="contained" disabled={saving} onClick={onSave}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
                Save details
              </Button>
              <Button variant="contained" color="success" disabled={boarding}
                onClick={() => boardMutation({ variables: { restaurantId } })}
                startIcon={boarding ? <CircularProgress size={14} color="inherit" /> : null}>
                Board restaurant
              </Button>
              <Button variant="outlined" disabled={refreshing || !boardInfo?.qorMerchantId}
                onClick={() => refreshMutation({ variables: { restaurantId } })}
                startIcon={refreshing ? <CircularProgress size={14} /> : null}>
                Refresh status
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  )
}
